import { useContext, useEffect, useRef, useState } from "react";
import { Footer } from "../components/Footer";
import { Header } from "../components/Header";
import { AppContext } from "../App";
import { CenterMap } from "../components/CenterMap";
import { orderCentersByDistance } from "../utils/helpers.js";

const defaultCenter = {
	lat: 40.416775,
	lng: -3.70379,
};

export const SelectCenter = () => {
	const nextPage = "damageGlass";
	const previousPage = "bookingForm";
	const { data } = useContext(AppContext);

	return (
		<>
			<Header />
			<Content nextPage={nextPage} />
			{data.viewMap == null && (
				<Footer nextPage={nextPage} previousPage={previousPage} />
			)}
		</>
	);
};

const Content = ({ nextPage }) => {
	const {
		data,
		setData,
		centers,
		setCenters,
		setFilteredCenters,
		visibleCenters,
		isTransitioning,
		navigate,
	} = useContext(AppContext);
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedCenter, setSelectedCenter] = useState(null);
	const [mapCenter, setMapCenter] = useState(defaultCenter);
	const [mapIsLoaded, setMapIsLoaded] = useState(false);
	const mapRef = useRef(null);
	const centerRefs = useRef({});

	const [viewOption, setViewOption] = useState("map_view");

	const filterCentersByDistance = (userCoords, maxDistanceKm = 100) => {
		const centersWithDistance = orderCentersByDistance(
			centers,
			userCoords
		).filter((c) => c.distance <= maxDistanceKm);

		setFilteredCenters(centersWithDistance);

		if (centersWithDistance.length > 0) {
			const closest = centersWithDistance[0];
			setSelectedCenter(closest);
			setData((prev) => ({
				...prev,
				centerId: closest.id,
				centerName: closest.name,
				centerCode: closest.code,
			}));

			if (mapRef.current) {
				mapRef.current.panTo({
					lat: closest.latitude,
					lng: closest.longitude,
				});
				mapRef.current.setZoom(12);
			}
		}
	};

	const searchQueryHandler = () => {
		if (!searchQuery.trim()) {
			setFilteredCenters(centers);
			return;
		}

		const geocoder = new window.google.maps.Geocoder();

		geocoder.geocode(
			{ address: searchQuery + ", Spain" },
			(results, status) => {
				if (status === "OK" && results[0]) {
					const location = results[0].geometry.location;
					const coords = {
						lat: location.lat(),
						lng: location.lng(),
					};
					filterCentersByDistance(coords, 100);
					setMapCenter(coords);
				} else {
					console.warn("Geocoding error:", status);
					setFilteredCenters([]);
				}
			}
		);
	};

	const selectCenter = (center) => () => {
		setSelectedCenter(center);
		setMapCenter({
			lat: center.latitude,
			lng: center.longitude,
		});
		setData({
			...data,
			centerId: center.id,
			centerName: center.name,
			centerCode: center.code,
		});
		if (viewOption === "map_view" && window.innerWidth < 768) {
			navigate(nextPage);
		}
	};

	useEffect(() => {
		if (centers.length === 0) {
			let API_URL =
				process.env.REACT_APP_API_URL || "http://localhost:3000";

			let controller = new AbortController();
			let options = {
				method: "GET",
				headers: { "Content-Type": "application/json" },
				signal: controller.signal,
			};

			let isMounted = true;

			fetch(`${API_URL}/query/workcenters`, options)
				.then((res) => res.json())
				.then((newData) => {
					if (isMounted && newData.result === "ok") {
						const workcenters = newData.workcenters.filter(
							(center) => center.active
						);
						setCenters(workcenters);
						setFilteredCenters(workcenters);
					}
				})
				.catch((err) => {
					if (err.name !== "AbortError") console.error(err);
				});

			return () => {
				isMounted = false;
				controller.abort();
			};
		}
	}, [centers.length, setCenters, setFilteredCenters]);

	useEffect(() => {
		if (
			data.viewMap &&
			centers.length > 0 &&
			window.google?.maps &&
			mapIsLoaded
		) {
			const geocoder = new window.google.maps.Geocoder();

			geocoder.geocode(
				{ address: data.viewMap + ", Spain" },
				(results, status) => {
					if (status === "OK" && results[0]) {
						const location = results[0].geometry.location;
						const coords = {
							lat: location.lat(),
							lng: location.lng(),
						};

						const centersWithDistance = orderCentersByDistance(
							centers,
							coords
						).filter((c) => c.distance <= 100);

						setFilteredCenters(centersWithDistance);

						if (centersWithDistance.length > 0) {
							const closest = centersWithDistance[0];
							setSelectedCenter(closest);
							setData((prev) => ({
								...prev,
								centerId: closest.id,
								centerName: closest.name,
								centerCode: closest.code,
							}));

							if (mapRef.current) {
								mapRef.current.panTo({
									lat: closest.latitude,
									lng: closest.longitude,
								});
								mapRef.current.setZoom(12);
							}
						}

						setMapCenter(coords);
						if (data.viewMap !== "none")
							setSearchQuery(data.viewMap);
					} else {
						console.warn("Geocoding error (viewMap):", status);
						setFilteredCenters([]);
					}
				}
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data.viewMap, centers, mapIsLoaded]);

	return (
		<div
			className={
				"content center_selector kensei_03" +
				(isTransitioning ? " fade-in" : "")
			}
		>
			<div className="content_select_center">
				{data.viewMap == null && (
					<>
						<h2 className="select_center_title">
							<strong>Selecciona un taller</strong>
						</h2>
						<div className="center_options">
							<form
								className="search"
								onSubmit={(e) => {
									e.preventDefault();
									searchQueryHandler();
								}}
								role="search"
							>
								<div className="search-icon">
									<img
										src="resources/icons/location.svg"
										alt="Ubicacion"
									/>
								</div>
								<input
									className="search-input"
									type="text"
									name="searchCenters"
									placeholder="Buscar por código postal o provincia"
									value={searchQuery}
									onChange={(e) => {
										const value = e.target.value;
										const capitalized = value.replace(
											/\b\w/g,
											(char) => char.toUpperCase()
										);
										setSearchQuery(capitalized);
									}}
								/>
								<button className="search-submit" type="submit">
									Buscar
								</button>
							</form>
						</div>
					</>
				)}
				<div className="center_layout">
					<div
						className={
							"center_map" +
							(viewOption === "map_view" ? " isActive" : "")
						}
					>
						<CenterMap
							selectedCenter={selectedCenter}
							setSelectedCenter={setSelectedCenter}
							filterCentersByDistance={filterCentersByDistance}
							selectCenter={selectCenter}
							mapCenter={mapCenter}
							setMapCenter={setMapCenter}
							defaultCenter={defaultCenter}
							mapRef={mapRef}
							setSearchQuery={setSearchQuery}
							setMapIsLoaded={setMapIsLoaded}
						/>
					</div>
					<div
						className={
							"center_lists" +
							(viewOption === "list_view" ? " isActive" : "")
						}
					>
						{visibleCenters.length > 0 ? (
							<span>
								Mostrando talleres cercanos a{" "}
								{searchQuery
									? '"' + searchQuery + '"'
									: "tu ubicación"}
							</span>
						) : (
							<span>No se han encontrado talleres</span>
						)}
						<ul className="filter_results">
							{visibleCenters.map((center) => (
								<li
									key={center.id}
									ref={(el) =>
										(centerRefs.current[center.id] = el)
									}
									className={
										"kensei_03" +
										(selectedCenter?.id === center.id ||
										data.centerId === center.id
											? " selected"
											: "")
									}
								>
									<h2 className="center_name">
										{center.name}
									</h2>
									<div className="center_address">
										<img
											src="resources/icons/location.svg"
											alt="Direccion"
										/>
										<p>{center.address}</p>
									</div>
									<p className="schedules">
										Lunes a viernes: {center.morningFrom} -{" "}
										{center.morningTo} y{" "}
										{center.eveningFrom} -{" "}
										{center.eveningTo}
										<br />
										Sábado:{" "}
										{["00:00", ""].includes(
											center.saturdayFrom
										) &&
										["00:00", ""].includes(
											center.saturdayTo
										)
											? "Cerrado"
											: `${center.saturdayFrom}${
													center.saturdayTo
														? ` - ${center.saturdayTo}`
														: ""
											  }`}
									</p>
									{data.viewMap != null ? (
										<a
											href={
												"https://www.ralarsa.com/es/taller-lunas-coche/" +
												center.slug
											}
											title={center.name}
											target="_top"
											rel="noopener noreferrer"
										>
											Ver taller
										</a>
									) : (
										<button
											onPointerDown={selectCenter(center)}
										>
											Seleccionar taller
										</button>
									)}
								</li>
							))}
						</ul>
					</div>
					<div className="view_options">
						<div
							className={
								"view_option" +
								(viewOption === "map_view" ? " isActive" : "")
							}
							onPointerDown={() => setViewOption("list_view")}
						>
							<img
								src="resources/icons/list.svg"
								alt="Lista"
								className="view_option_icon"
							/>
							<span>Lista</span>
						</div>
						<div
							className={
								"view_option" +
								(viewOption === "list_view" ? " isActive" : "")
							}
							onPointerDown={() => setViewOption("map_view")}
						>
							<img
								src="resources/icons/map.svg"
								alt="Mapa"
								className="view_option_icon"
							/>
							<span>Mapa</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
